import * as React from 'react';
import { FixMeLater } from '../../utils';
import { ButtonBase, ButtonBaseProps } from '../ButtonBase';
import { FieldBase, FieldBaseProps } from '../FieldBase';
import { StylerProps } from '../Styler';

export interface ButtonFieldProps
    extends Pick<
            FieldBaseProps,
            | 'fullWidth'
            | 'label'
            | 'shrinkLabel'
            | 'icon'
            | 'textAlign'
            | 'size'
            | 'variant'
            | 'showClear'
            | 'buttonIcon'
            | 'onButtonClick'
            | 'buttonRef'
            | 'width'
            | 'validationStatus'
        >,
        ButtonBaseProps {
    /**
     * A reference passed to the `button` element.
     */
    buttonRef?: React.Ref<FixMeLater>;

    /**
     * The hint text displayed before a value has been entered.
     */
    placeholder?: string;

    /**
     * The text which represents the value of the field.
     */
    value?: string;

    /**
     * Additional properties applied to the field's root element.
     */
    FieldRootProps?: StylerProps;
}

/**
 * Button field is an interactive field type which supports for actions on click of the field, it acts as a button but also as a text field at the same time.
 * It can be viewed as a combination of both button and text field with all the behaviors such as disabled, fullWidth, highlighted and various state representation. On top
 * of these custom features the field can be clearable with use of showClear prop which allows the field to be emptied on click.
 */
export function ButtonField(props: Readonly<ButtonFieldProps>): React.JSX.Element {
    const {
        buttonRef,
        disabled,
        fullWidth,
        id,
        label,
        placeholder,
        size,
        value,
        shrinkLabel,
        variant,
        showClear,
        onButtonClick,
        buttonIcon,
        width,
        validationStatus,
        FieldRootProps,
        ref,
        ...other
    } = props;

    return (
        <FieldBase
            ref={ref}
            id={id}
            contains="button"
            disabled={disabled}
            label={label}
            fullWidth={fullWidth}
            size={size}
            shrinkLabel={shrinkLabel || Boolean(value || Boolean(placeholder))}
            variant={variant}
            showClear={showClear}
            buttonIcon={buttonIcon}
            onButtonClick={onButtonClick}
            width={width}
            validationStatus={validationStatus}
            {...FieldRootProps}
        >
            <ButtonBase ref={buttonRef} isFieldBaseControl={true} disabled={disabled} {...other}>
                {value || placeholder}
            </ButtonBase>
        </FieldBase>
    );
}
